import { useState, useCallback, useMemo, memo, useEffect } from "react";
import { LayoutGrid, X as LucideX, UserCircle, LogOut, Settings, User, Plus, ShoppingCart, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calculator, Bell, Calendar, X } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDispatch, useSelector } from "react-redux";
import { toggle } from "@/state/toggle/toggle.slice";
import { logout } from "@/state/auth/auth.slice";
import { greet } from "@/utils/greetings";
// Using useNavigate from react-router-dom instead of next/navigation
import { useToast } from "@/components/ui/use-toast";
import ThemeToggle from "@/components/custom/ThemeToggle";

// Memoized calculator buttons - defined outside component to avoid recreation
const CALCULATOR_BUTTONS = [
  "7", "8", "9", "/",
  "4", "5", "6", "*",
  "1", "2", "3", "-",
  "0", ".", "=", "+",
  "C",
];

// Memoized Calculator Button component to prevent unnecessary re-renders
const CalculatorButton = memo(({ value, onClick }) => (
  <Button
    onClick={() => onClick(value)}
    className={`h-12 ${value === "=" ? "col-span-2 bg-orange-400 text-2xl" : ""}`}
  >
    {value}
  </Button>
));
CalculatorButton.displayName = "CalculatorButton";

// Safe math evaluation without using eval
const evaluateExpression = (expression) => {
  try {
    // Only allow numbers and basic operators
    if (!/^[0-9+\-*/.() ]+$/.test(expression)) {
      return "Error";
    }
    // Using Function constructor as a safer alternative to eval
    const result = new Function(`return (${expression})`)();
    return isFinite(result) ? result.toString() : "Error";
  } catch {
    return "Error";
  }
};

// Format date consistently to avoid hydration mismatch
const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const Header = memo(() => {
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [calculatorValue, setCalculatorValue] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [todayDate, setTodayDate] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Memoize selectors
  const isOpen = useSelector((state) => state.toggle.isOpen);
  const user = useSelector((state) => state.auth.user);

  // Memoize greeting message
  const greetingMessage = useMemo(() => greet(user?.username), [user?.username]);

  // Set date on client side only to avoid hydration mismatch
  useEffect(() => {
    setTodayDate(formatDate(new Date()));
  }, []);

  // Get user initials for avatar
  const userInitials = useMemo(() => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.username?.substring(0, 2).toUpperCase() || "U";
  }, [user]);

  // Memoize toggle handler
  const handleToggle = useCallback(() => {
    dispatch(toggle());
  }, [dispatch]);

  // Memoize calculator input handler
  const handleCalculatorInput = useCallback((value) => {
    if (value === "=") {
      setCalculatorValue((prev) => evaluateExpression(prev));
    } else if (value === "C") {
      setCalculatorValue("");
    } else {
      setCalculatorValue((prev) => prev + value);
    }
  }, []);

  // Memoize calculator dialog open change handler
  const handleCalculatorOpenChange = useCallback((open) => {
    setIsCalculatorOpen(open);
    if (!open) {
      setCalculatorValue(""); // Clear on close
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      setIsLoggingOut(true);
      await dispatch(logout()).unwrap();
      toast({ title: "Success", description: "Logged out successfully" });
      navigate("/");
    } catch (error) {
      setIsLoggingOut(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to logout"
      });
    } finally {
      navigate("/");
      setIsLoggingOut(false);
    }
  }, [dispatch, toast]);

  // Navigation handlers
  const handleNavigateToProfile = useCallback(() => {
    navigate("/dashboard/settings/profile");
  }, [navigate]);

  const handleNavigateToSettings = useCallback(() => {
    navigate("/dashboard/settings/business");
  }, [navigate]);

  return (
    <header className="bg-card border-b border-border shadow-sm sticky top-0 z-20 w-full">
      <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Menu toggle */}
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="mr-4"
              onClick={handleToggle}
            >
              {isOpen ? (
                <LucideX className="h-6 w-6 text-red-500" />
              ) : (
                <LayoutGrid className="h-6 w-6" />
              )}
            </Button>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center p-2">
            <h1 className="text-sm mr-4 hidden xl:block font-light italic text-foreground">
              {greetingMessage}
            </h1>

            {/* Quick Actions */}
            <div className="flex items-center gap-1 mr-2">
              <Link to="/dashboard/sales/create">
                <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700 text-white">
                  <ShoppingCart className="h-4 w-4" />
                  <span className="hidden sm:inline">New Sale</span>
                </Button>
              </Link>
              <Link to="/dashboard/products/add-product">
                <Button size="sm" variant="outline" className="gap-1.5">
                  <Package className="h-4 w-4" />
                  <span className="hidden md:inline">Add Product</span>
                </Button>
              </Link>
            </div>

            {/* Calculator Dialog */}
            <Dialog open={isCalculatorOpen} onOpenChange={handleCalculatorOpenChange}>
              <DialogTrigger asChild>
                <Button size="icon" className="mr-1 md:mr-2">
                  <Calculator className="h-5 w-5 md:h-6 md:w-6" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Calculator</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4">
                  <input
                    type="text"
                    value={calculatorValue}
                    readOnly
                    className="w-full p-2 text-right text-2xl font-bold bg-gray-100 dark:bg-slate-800 rounded"
                    placeholder="0"
                  />
                  <div className="grid grid-cols-4 gap-2">
                    {CALCULATOR_BUTTONS.map((btn) => (
                      <CalculatorButton
                        key={btn}
                        value={btn}
                        onClick={handleCalculatorInput}
                      />
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Notifications */}
            <Button onClick={() => navigate("/dashboard/reports/activities")} size="icon" className="mr-1 md:mr-2 bg-black">
              <Bell className="h-5 w-5 md:h-6 md:w-6" />
            </Button>

            {/* Date Display */}
            <Button className="mr-1 md:mr-2 px-2 md:px-4" variant="outline">
              <Calendar className="h-5 w-5 md:h-6 md:w-6 md:mr-2" />
              <span className="hidden md:inline">{todayDate}</span>
            </Button>

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* User Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.avatar} alt={user?.username} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.first_name && user?.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user?.username || "User"
                      }
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || "No email"}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleNavigateToProfile} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleNavigateToSettings} className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {isLoggingOut && (
        <div className="fixed inset-0 bg-background/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent animate-spin mb-4"></div>
            <p className="text-foreground font-semibold">Logging out...</p>
          </div>
        </div>
      )
      }
    </header>
  );
});

Header.displayName = "Header";

export default Header;
